/**
 * Camera and Microphone Help Component
 * Shows permission help information in the profile settings
 */

import React, { useState, useEffect, useRef } from 'react'
import { Box, Typography, Paper, Button, Alert, Grid } from '@mui/material'
import VideocamIcon from '@mui/icons-material/Videocam'
import MicIcon from '@mui/icons-material/Mic'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import MicOffIcon from '@mui/icons-material/MicOff'
import { useMediaPermissions } from 'src/customHooks/mediaPermissions'
import { PermissionStatusIndicator, PermissionHelpDrawer, BrowserSpecificHelp } from 'src/components/MediaPermissions'
import { generateRecoveryPlan, PERMISSION_STATES, REQUEST_RESULTS, MEDIA_ERROR_CODES } from 'src/services/mediaPermissions'

export const CameraAndMicrophoneHelp = () => {
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [cameraStream, setCameraStream] = useState(null)
  const [microphoneStream, setMicrophoneStream] = useState(null)
  const [audioLevel, setAudioLevel] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)
  const videoRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const animationFrameRef = useRef(null)
  
  const {
    cameraPermission,
    microphonePermission,
    isGranted,
    checkPermissions,
    requestPermissions,
    isChecking,
    isRequesting,
  } = useMediaPermissions({
    mediaType: 'both',
    autoCheck: false, // Disable automatic checking - only check manually
  })

  // Force refresh handler
  const handleRefresh = async () => {
    setRefreshKey(prev => prev + 1)
    await checkPermissions()
  }

  // Get user-friendly error message
  const getErrorMessage = (error, deviceType = 'device') => {
    const errorName = error?.name || error?.error?.name || ''
    
    if (errorName === MEDIA_ERROR_CODES.NOT_ALLOWED || errorName === 'PermissionDeniedError') {
      return {
        title: 'Permission Denied',
        message: `Camera access was denied. Please allow camera access in your browser settings and try again.`,
        action: 'Click "View Detailed Instructions" below for step-by-step help.',
      }
    }
    
    if (errorName === MEDIA_ERROR_CODES.NOT_FOUND || errorName === 'NotFoundError') {
      return {
        title: 'Camera Not Found',
        message: `No camera was detected on your device. Please make sure a camera is connected and try again.`,
        action: 'Check that your camera is properly connected.',
      }
    }
    
    if (errorName === MEDIA_ERROR_CODES.NOT_READABLE || errorName === 'NotReadableError') {
      return {
        title: 'Camera In Use',
        message: `Your camera is currently being used by another application. Please close other apps using the camera and try again.`,
        action: 'Close other applications that might be using your camera.',
      }
    }
    
    if (errorName === MEDIA_ERROR_CODES.SECURITY || errorName === 'SecurityError') {
      return {
        title: 'Security Error',
        message: `Camera access is blocked due to security settings. Please check your browser's security settings.`,
        action: 'Make sure you\'re using HTTPS and check browser security settings.',
      }
    }
    
    // Default error
    return {
      title: 'Unable to Access Camera',
      message: `Failed to access your camera. Error: ${error?.message || errorName || 'Unknown error'}`,
      action: 'Please check your browser settings and try again.',
    }
  }

  // Request camera permission and start preview
  const handleRequestCamera = async () => {
    // Clear any previous errors
    setErrorMessage(null)
    
    try {
      // Check current permission state first
      let permissionState = PERMISSION_STATES.UNKNOWN
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const permission = await navigator.permissions.query({ name: 'camera' })
          permissionState = permission.state
        }
      } catch (e) {
        // Permissions API not supported, proceed with getUserMedia
      }

      // If already granted, just start the preview
      if (permissionState === PERMISSION_STATES.GRANTED && cameraStream) {
        // Already have stream, just refresh
        await checkPermissions()
        return
      }

      // If denied, show helpful message
      if (permissionState === PERMISSION_STATES.DENIED) {
        setErrorMessage({
          title: 'Camera Permission Blocked',
          message: 'Camera access has been blocked. Please enable it in your browser settings.',
          action: 'Click the lock icon in your browser\'s address bar, then allow camera access.',
        })
        setTimeout(() => setErrorMessage(null), 15000)
        await checkPermissions()
        return
      }

      // Request permission - this will show the system popup if in "prompt" state
      // or if permission state is unknown
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }, 
        audio: false 
      })
      
      setCameraStream(stream)
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
      
      // Update permissions after successful request
      await checkPermissions()
    } catch (error) {
      console.error('Error requesting camera:', error)
      const errorInfo = getErrorMessage(error, 'camera')
      setErrorMessage({
        title: errorInfo.title,
        message: errorInfo.message,
        action: errorInfo.action,
      })
      // Clear error after 15 seconds
      setTimeout(() => setErrorMessage(null), 15000)
      await checkPermissions()
    }
  }

  // Get user-friendly error message for microphone
  const getMicrophoneErrorMessage = (error) => {
    const errorName = error?.name || error?.error?.name || ''
    
    if (errorName === MEDIA_ERROR_CODES.NOT_ALLOWED || errorName === 'PermissionDeniedError') {
      return {
        title: 'Permission Denied',
        message: `Microphone access was denied. Please allow microphone access in your browser settings and try again.`,
        action: 'Click "View Detailed Instructions" below for step-by-step help.',
      }
    }
    
    if (errorName === MEDIA_ERROR_CODES.NOT_FOUND || errorName === 'NotFoundError') {
      return {
        title: 'Microphone Not Found',
        message: `No microphone was detected on your device. Please make sure a microphone is connected and try again.`,
        action: 'Check that your microphone is properly connected.',
      }
    }
    
    if (errorName === MEDIA_ERROR_CODES.NOT_READABLE || errorName === 'NotReadableError') {
      return {
        title: 'Microphone In Use',
        message: `Your microphone is currently being used by another application. Please close other apps using the microphone and try again.`,
        action: 'Close other applications that might be using your microphone.',
      }
    }
    
    if (errorName === MEDIA_ERROR_CODES.SECURITY || errorName === 'SecurityError') {
      return {
        title: 'Security Error',
        message: `Microphone access is blocked due to security settings. Please check your browser's security settings.`,
        action: 'Make sure you\'re using HTTPS and check browser security settings.',
      }
    }
    
    // Default error
    return {
      title: 'Unable to Access Microphone',
      message: `Failed to access your microphone. Error: ${error?.message || errorName || 'Unknown error'}`,
      action: 'Please check your browser settings and try again.',
    }
  }

  // Request microphone permission and start audio visualization
  const handleRequestMicrophone = async () => {
    // Clear any previous errors
    setErrorMessage(null)
    
    try {
      // Check current permission state first
      let permissionState = PERMISSION_STATES.UNKNOWN
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const permission = await navigator.permissions.query({ name: 'microphone' })
          permissionState = permission.state
        }
      } catch (e) {
        // Permissions API not supported, proceed with getUserMedia
      }

      // If already granted, just start the visualization
      if (permissionState === PERMISSION_STATES.GRANTED && microphoneStream) {
        // Already have stream, just refresh
        await checkPermissions()
        return
      }

      // If denied, show helpful message
      if (permissionState === PERMISSION_STATES.DENIED) {
        setErrorMessage({
          title: 'Microphone Permission Blocked',
          message: 'Microphone access has been blocked. Please enable it in your browser settings.',
          action: 'Click the lock icon in your browser\'s address bar, then allow microphone access.',
        })
        setTimeout(() => setErrorMessage(null), 15000)
        await checkPermissions()
        return
      }

      // Request permission - this will show the system popup if in "prompt" state
      // or if permission state is unknown
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }, 
        video: false 
      })
      
      setMicrophoneStream(stream)
      
      // Set up audio visualization
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const analyser = audioContext.createAnalyser()
      const microphone = audioContext.createMediaStreamSource(stream)
      
      analyser.fftSize = 256
      microphone.connect(analyser)
      
      audioContextRef.current = audioContext
      analyserRef.current = analyser
      
      // Start audio level monitoring
      const updateAudioLevel = () => {
        if (analyserRef.current) {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
          analyserRef.current.getByteFrequencyData(dataArray)
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length
          setAudioLevel(average)
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel)
        }
      }
      updateAudioLevel()
      
      // Update permissions after successful request
      await checkPermissions()
    } catch (error) {
      console.error('Error requesting microphone:', error)
      const errorInfo = getMicrophoneErrorMessage(error)
      setErrorMessage({
        title: errorInfo.title,
        message: errorInfo.message,
        action: errorInfo.action,
      })
      // Clear error after 15 seconds
      setTimeout(() => setErrorMessage(null), 15000)
      await checkPermissions()
    }
  }

  // Request both permissions
  const handleRequestBoth = async () => {
    // Clear any previous errors
    setErrorMessage(null)
    
    try {
      // Directly call getUserMedia to trigger system popup
      // This ensures the browser's native permission dialog appears
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }, 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })
      
      // Set up camera preview
      const videoTracks = stream.getVideoTracks()
      if (videoTracks.length > 0) {
        const videoStream = new MediaStream(videoTracks)
        setCameraStream(videoStream)
        if (videoRef.current) {
          videoRef.current.srcObject = videoStream
        }
      }
      
      // Set up microphone visualization
      const audioTracks = stream.getAudioTracks()
      if (audioTracks.length > 0) {
        const audioStream = new MediaStream(audioTracks)
        setMicrophoneStream(audioStream)
        
        if (audioContextRef.current) {
          audioContextRef.current.close()
        }
        
        const audioContext = new (window.AudioContext || window.webkitAudioContext)()
        const analyser = audioContext.createAnalyser()
        const microphone = audioContext.createMediaStreamSource(stream)
        
        analyser.fftSize = 256
        microphone.connect(analyser)
        
        audioContextRef.current = audioContext
        analyserRef.current = analyser
        
        const updateAudioLevel = () => {
          if (analyserRef.current) {
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
            analyserRef.current.getByteFrequencyData(dataArray)
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length
            setAudioLevel(average)
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel)
          }
        }
        updateAudioLevel()
      }
      
      // Update permissions after successful request
      await checkPermissions()
    } catch (error) {
      console.error('Error requesting both permissions:', error)
      
      // Check if it's a permission denial
      if (error?.name === MEDIA_ERROR_CODES.NOT_ALLOWED || error?.name === 'PermissionDeniedError') {
        setErrorMessage({
          title: 'Permission Denied',
          message: 'Camera and microphone access was denied. Please allow access in your browser settings.',
          action: 'Click the lock icon in your browser\'s address bar, then allow camera and microphone access. Or click "View Detailed Instructions" below.',
        })
      } else {
        const errorInfo = getErrorMessage(error, 'camera and microphone')
        setErrorMessage({
          title: errorInfo.title,
          message: errorInfo.message,
          action: errorInfo.action,
        })
      }
      
      setTimeout(() => setErrorMessage(null), 15000)
      await checkPermissions()
    }
  }

  // Stop camera preview
  const handleStopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop())
      setCameraStream(null)
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }

  // Stop microphone
  const handleStopMicrophone = () => {
    if (microphoneStream) {
      microphoneStream.getTracks().forEach(track => track.stop())
      setMicrophoneStream(null)
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    if (analyserRef.current) {
      analyserRef.current = null
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    setAudioLevel(0)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleStopCamera()
      handleStopMicrophone()
    }
  }, [])

  const recoveryPlan = generateRecoveryPlan('both', false)
  
  const cameraIsGranted = cameraPermission === PERMISSION_STATES.GRANTED
  const microphoneIsGranted = microphonePermission === PERMISSION_STATES.GRANTED

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 800,
        margin: '0 auto',
        padding: 3,
        color: '#FFF',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 2,
            backgroundColor: 'rgba(81, 67, 135, 0.2)',
            borderRadius: 2,
          }}
        >
          <VideocamIcon sx={{ color: '#FFF', fontSize: 32 }} />
          <MicIcon sx={{ color: '#FFF', fontSize: 32 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="#FFF" gutterBottom>
            Camera & Microphone Help
          </Typography>
          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
            Manage permissions for video calls and consultations
          </Typography>
        </Box>
      </Box>

      {/* Current Status Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="#FFF" gutterBottom>
          Current Permission Status
        </Typography>
        
        <Box sx={{ my: 2 }} key={refreshKey}>
          <PermissionStatusIndicator
            cameraPermission={cameraPermission}
            microphonePermission={microphonePermission}
            showLabels={true}
            size="medium"
          />
        </Box>

        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={isChecking}
          sx={{
            mt: 2,
            color: '#FFF',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              borderColor: '#FFF',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {isChecking ? 'Checking...' : 'Refresh Status'}
        </Button>
        
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            mt: 1, 
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '12px'
          }}
        >
          Status updates automatically. Click refresh to check manually.
        </Typography>
      </Paper>

      {/* Enable Permissions Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="#FFF" gutterBottom>
          Enable Permissions
        </Typography>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 3 }}>
          Click the buttons below to request and test camera and microphone permissions. You'll see a preview when permissions are granted.
        </Typography>

        <Grid container spacing={2}>
          {/* Camera Section */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {cameraIsGranted ? (
                  <VideocamIcon sx={{ color: '#4CAF50', fontSize: 28 }} />
                ) : (
                  <VideocamOffIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 28 }} />
                )}
                <Typography variant="subtitle1" fontWeight="bold" color="#FFF">
                  Camera
                </Typography>
              </Box>

              {cameraStream ? (
                <Box sx={{ mb: 2 }}>
                  <Box
                    component="video"
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      borderRadius: 2,
                      backgroundColor: '#000',
                      aspectRatio: '16/9',
                      objectFit: 'cover',
                    }}
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleStopCamera}
                    sx={{
                      mt: 1,
                      color: '#FFF',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      '&:hover': {
                        borderColor: '#FFF',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    Stop Preview
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleRequestCamera}
                  disabled={isRequesting}
                  startIcon={cameraIsGranted ? <VideocamIcon /> : <VideocamOffIcon />}
                  sx={{
                    backgroundColor: cameraIsGranted ? '#4CAF50' : '#514387',
                    color: '#FFF',
                    '&:hover': {
                      backgroundColor: cameraIsGranted ? '#45a049' : '#403266',
                    },
                  }}
                >
                  {cameraIsGranted ? 'Test Camera' : 'Enable Camera'}
                </Button>
              )}
            </Box>
          </Grid>

          {/* Microphone Section */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {microphoneIsGranted ? (
                  <MicIcon sx={{ color: '#4CAF50', fontSize: 28 }} />
                ) : (
                  <MicOffIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 28 }} />
                )}
                <Typography variant="subtitle1" fontWeight="bold" color="#FFF">
                  Microphone
                </Typography>
              </Box>

              {microphoneStream ? (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 60,
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${Math.min(audioLevel * 2, 100)}%`,
                        backgroundColor: '#4CAF50',
                        transition: 'width 0.1s ease',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      {audioLevel > 10 ? '🎤 Speaking...' : '🎤 Listening...'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleStopMicrophone}
                    sx={{
                      color: '#FFF',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      '&:hover': {
                        borderColor: '#FFF',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    Stop Test
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleRequestMicrophone}
                  disabled={isRequesting}
                  startIcon={microphoneIsGranted ? <MicIcon /> : <MicOffIcon />}
                  sx={{
                    backgroundColor: microphoneIsGranted ? '#4CAF50' : '#514387',
                    color: '#FFF',
                    '&:hover': {
                      backgroundColor: microphoneIsGranted ? '#45a049' : '#403266',
                    },
                  }}
                >
                  {microphoneIsGranted ? 'Test Microphone' : 'Enable Microphone'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>

        <Button
          variant="contained"
          fullWidth
          onClick={handleRequestBoth}
          disabled={isRequesting}
          startIcon={<VideocamIcon />}
          sx={{
            mt: 2,
            backgroundColor: '#514387',
            color: '#FFF',
            '&:hover': {
              backgroundColor: '#403266',
            },
          }}
        >
          {isRequesting ? 'Requesting...' : 'Enable Both Camera & Microphone'}
        </Button>
      </Paper>

      {/* Status Message */}
      {isGranted ? (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          icon={<HelpOutlineIcon />}
        >
          Your camera and microphone are enabled and ready for video calls!
        </Alert>
      ) : (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          icon={<HelpOutlineIcon />}
        >
          Some permissions are not enabled. You may need to allow access before joining video calls.
        </Alert>
      )}

      {/* Error Message */}
      {errorMessage && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setErrorMessage(null)}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {errorMessage.title}
          </Typography>
          <Typography variant="body2" paragraph>
            {errorMessage.message}
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {errorMessage.action}
          </Typography>
        </Alert>
      )}

      {/* Quick Help Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="#FFF" gutterBottom>
          Need Help?
        </Typography>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.8)" paragraph>
          If you're having trouble with camera or microphone permissions, click below for detailed instructions specific to your browser.
        </Typography>
        
        <Button
          variant="contained"
          fullWidth
          onClick={() => setHelpDrawerOpen(true)}
          sx={{
            backgroundColor: '#514387',
            color: '#FFF',
            '&:hover': {
              backgroundColor: '#403266',
            },
            mt: 2,
          }}
        >
          View Detailed Instructions
        </Button>
      </Paper>

      {/* Why We Need Permissions */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="#FFF" gutterBottom>
          Why We Need These Permissions
        </Typography>
        <Box component="ul" sx={{ pl: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
          <li>
            <Typography variant="body2" paragraph>
              <strong>Camera:</strong> Allows your practitioner to see you during video consultations, creating a more personal and effective session.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" paragraph>
              <strong>Microphone:</strong> Enables voice communication during sessions, allowing you to speak freely with your practitioner.
            </Typography>
          </li>
        </Box>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 2 }}>
          <strong>Your Privacy:</strong> You have full control over your camera and microphone. You can turn them off at any time during a call, and we never record or store your video/audio without your explicit consent.
        </Typography>
      </Paper>

      {/* Help Drawer */}
      <PermissionHelpDrawer
        open={helpDrawerOpen}
        onClose={() => setHelpDrawerOpen(false)}
        anchor="right"
      />
    </Box>
  )
}

export default CameraAndMicrophoneHelp


















